import { useState } from 'react';

import { geometryService } from '../../api/client';
import type { DrawingScene, ValidationMessage } from '../../gen/ejot/roofing/v1/types';
import { MessageList } from '../../shared/components';

/**
 * Downloads a backend drawing scene as DXF.
 *
 * The browser sends the scene the backend authored and saves whatever comes
 * back. It does not build DXF itself: a CAD file assembled in the interface
 * would be a fourth drawing that only looks like the other three.
 */
export function DxfExportButton({ scene, fileName }: { scene: DrawingScene | undefined; fileName: string }) {
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState<readonly ValidationMessage[]>([]);

  const available = Boolean(scene && (scene.primitives?.length ?? 0) > 0);

  const download = async () => {
    if (!scene || busy) return;
    setBusy(true);
    setMessages([]);
    try {
      const response = await geometryService.exportDrawing({
        scene,
        format: 'DRAWING_EXPORT_FORMAT_DXF',
        file_name: fileName,
      });
      if (!response.content) {
        setMessages(response.messages ?? [{
          code: 'DRAWING_EXPORT_EMPTY',
          severity: 'SEVERITY_ERROR',
          category: 'MESSAGE_CATEGORY_GEOMETRY',
          title: 'No drawing was exported',
          explanation: 'The backend returned no document for this drawing.',
        }]);
        return;
      }
      saveTextFile(response.content, response.file_name || `${fileName}.dxf`, response.content_type);
    } catch (error) {
      setMessages([{
        code: 'DRAWING_EXPORT_FAILED',
        severity: 'SEVERITY_ERROR',
        category: 'MESSAGE_CATEGORY_GEOMETRY',
        title: 'Drawing export failed',
        explanation: error instanceof Error ? error.message : 'The export request did not complete.',
        required_action: 'Check the connection to the backend and try again.',
      }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="drawing-export">
      <button
        type="button"
        className="text-button"
        disabled={!available || busy}
        onClick={() => { void download(); }}
      >
        {busy ? 'Exporting DXF…' : 'Export DXF'}
      </button>
      {!available && (
        <span className="field-note">
          A DXF is available once the backend produces this drawing.
        </span>
      )}
      {messages.length > 0 && <MessageList messages={messages} emptyText="" />}
    </div>
  );
}

function saveTextFile(content: string, name: string, contentType: string | undefined) {
  const blob = new Blob([content], { type: contentType || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
